/**
 * Phase 19 / Task 19.2 (DEC-325) — data for the Operations dashboard.
 *
 * GET /v1/dashboards/operations. Types mirror packages/api/src/routes/dashboards/operations.ts
 * and services/metrics/operations-dashboard.ts (copied by hand, like every web-dispatch hook).
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

export interface LiveOperations {
  readonly drivers: {
    readonly onShift: number;
    readonly paused: number;
    readonly offShift: number;
  };
  readonly jobs: {
    readonly unassigned: number;
    readonly notStarted: number;
    readonly inProgress: number;
  };
  readonly completedToday: number;
  /** Net BBL as billed, 2 decimals, as a string. */
  readonly bblToday: string;
  readonly attention: {
    readonly inReview: number;
    readonly stuck: number;
    readonly syncProblems: number;
  };
}

export type ComplianceField = 'CDL' | 'TWIC' | 'HAZMAT' | 'DOT_MEDICAL' | 'REGISTRATION';

export interface ComplianceItem {
  readonly kind: 'DRIVER' | 'TRUCK';
  readonly id: string;
  readonly name: string;
  readonly field: ComplianceField;
  /** 'YYYY-MM-DD' calendar date. */
  readonly expiresOn: string;
  /** Negative when already expired. */
  readonly daysRemaining: number;
}

export interface ComplianceExpiring {
  readonly withinDays: number;
  readonly expired: number;
  readonly expiringSoon: number;
  readonly items: readonly ComplianceItem[];
}

export interface DriverPerformanceRow {
  readonly driverId: string | null;
  readonly driverName: string | null;
  readonly loads: number;
  readonly bbl: string;
  readonly activeSecs: number;
  readonly loadsWithClock: number;
  readonly arrivalsVerified: number;
  readonly arrivalsTotal: number;
  readonly gpsVerifiedPct: number | null;
}

export interface DriverPerformance {
  readonly from: string;
  readonly to: string;
  readonly companyTimezone: string;
  readonly history: { readonly lastRunAt: string | null; readonly coveredThrough: string | null };
  readonly rows: readonly DriverPerformanceRow[];
}

export interface OperationsDashboardResponse {
  readonly generatedAt: string;
  readonly timezone: string;
  readonly live: LiveOperations;
  readonly compliance: ComplianceExpiring;
  readonly drivers: DriverPerformance;
}

export const OPERATIONS_DASHBOARD_QUERY_KEY = (
  tz: string,
  from: string,
  to: string,
): readonly ['dashboards', 'operations', string, string, string] =>
  ['dashboards', 'operations', tz, from, to] as const;

export function useOperationsDashboard(
  apiOpts: ReturnType<typeof useApiOpts>,
  tz: string,
  from: string,
  to: string,
): ReturnType<typeof useApiQuery<OperationsDashboardResponse>> {
  const qs = new URLSearchParams({ tz, from, to }).toString();
  return useApiQuery<OperationsDashboardResponse>(apiOpts, `/v1/dashboards/operations?${qs}`, {
    queryKey: OPERATIONS_DASHBOARD_QUERY_KEY(tz, from, to),
    refetchInterval: 60_000,
  });
}
